import {
  setShowSearchArea,
  addQueryToHistory,
  landownerResults,
  lastQueryInputFetchedLocationSearch,
  lastQueryInputFetchedLandownerSearch,
  locationResults,
  queryInput,
  searchLandownerByGraphQl,
  searchLocationByGraphQl,
  searchMode,
  searchOrigin,
  setSearchMode,
  setIsLoading,
  setLandownerResults,
  setLastQueryInputFetchedLandownerSearch,
  setLastQueryInputFetchedLocationSearch,
  setLocationResults,
  setQueryInput,
  setSearchCompleted,
  setSearchOrigin,
  setTotalLandownerResults,
  setTotalLocationResults,
  setGlyphIconMap,
  setSearchIsActive,
  searchIsActive,
  activeLastSearchTerm,
  clickedBackFromResultCard,
  setClickedBackFromResultCard,
  searchHistoryDisplay,
  convertCoordResultToApiResponseType,
  getStatesFilter,
  getIsoStateNames,
  isSearchPinsV2Enabled,
  handleAutocompleteSelection,
  handleSearchResultSelection,
} from '@/services/search-service'

import {
  parseInputForCoordinates,
  parseDirectionDdm,
  parseDdm,
  parseDirectionDD,
  parseDirectionDms,
  parseDms,
  parseUtm,
  validateLonLat,
} from '@onxmaps/search-web-coordinates'
import { EVENTS, SEARCH_ORIGINS } from '@/lib/enums-marketing'
import {
  acreageFilter,
  getFilterDefaults,
  hasMinMaxFilter,
  filterCount,
} from '@/services/search-filter-service'
import { SEARCH_FILTER_KEYS } from '@/services/search-filter-service-constants'
import { SearchMode } from '@/services/search-service/constants'
import debounce from 'lodash/debounce'
import Geographic from '@/lib/geographic'
import marketingEventService from '@/services/marketing-event-service'
import pubsub from '@/lib/pubsub'
import { generateGlyphMarkers } from '@/lib/glyph-markers'
import {
  camera,
  clearAllMarkers,
  replaceMarkers,
  setFeatureSelected,
  setSearchPinSelected,
} from '@/services/map-service'
import { GLYPH_FONTS } from '@/lib/glyph-markers-constants'

import { separateSearchResultsByCategories } from '@/services/search-service/categories'
import ComboboxNavigation from '@/lib/combobox-nav'
import { useHasAccess } from '@/services/feature-access/composables'
import { DomainFeatureGates } from '@/services/feature-access/global'
import {
  isSearchAutocompleteEnabled,
  fetchAutocompleteResults,
  type AutocompleteResult,
  type AutocompleteOptions,
} from '@/lib/search/search-autocomplete'

// Vue
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Types
import type { Position } from 'geojson'
import type { SearchResultProps } from '@/services/search-service/types'
import type { SearchResultItem } from '@onxmaps/search-web'
import {
  clearSearchResultPins,
  displaySearchResultPins,
  generatePinFeatureFromSearchResult,
} from '@/lib/search/search-result-pins'
import { useRouterState } from '@/composables/router-state'

// FUTURE: move to search-filter-service when that gets refactored to TS
type SearchFilterValues = {
  min: number
  max: number
}

/**
 * Options for configuring map search behavior.
 */
export type MapSearchOptions = {
  /**
   * Reference to the search HTMLElement (encompassing the search input and results UI)
   */
  searchId: string
  /**
   * Reference to the search results HTMLElement (encompassing the scrollable list of results)
   */
  resultsId?: string
  /**
   * When true, displays map markers for search results.
   */
  resultMapMarkers?: boolean
  /**
   * When search is active, uses the Search This Area feature when the map is moved
   */
  searchThisArea?: boolean
  /**
   * Any elements to manage keyboard focus for, as a comma delimeted list of classnames. ex: ".class1,.class2"
   */
  focusSelectors?: string
  /**
   * Callback to execute when the search input becomes focused
   */
  onFocus?: () => void
}

/**
 * Composable for managing user search input and orchestrating a search request
 * in order to return structured search results that can be leveraged by any search UI
 */
const useMapSearch = ({
  searchId,
  resultsId,
  resultMapMarkers = true,
  searchThisArea = true,
  focusSelectors = '',
  onFocus,
}: MapSearchOptions) => {
  let autocompleteRequestIndex = 0
  let locationRequestIndex = 0
  let landownerRequestIndex = 0
  let removeMarkersDisabled = false
  let keyboardNavigation: ComboboxNavigation | null = null
  let mouseDownInsideSearch = false

  const route = useRoute()
  const router = useRouter()
  const routerState = useRouterState()
  const { hasAccess: searchThisAreaAccess } = useHasAccess(
    DomainFeatureGates.search_this_area
  )
  const { hasAccess: searchMapMarkersAccess } = useHasAccess(
    DomainFeatureGates.search_map_markers
  )
  const { hasAccess: privateLandSearchAccess } = useHasAccess(
    DomainFeatureGates.private_land_search
  )

  /// REFS
  const error = ref<string | null>(null)
  const currentResultsGroup = ref<string>('All')
  const searchRef = ref<HTMLElement | null>(null)
  const resultsRef = ref<HTMLElement | null>(null)
  const autocompleteResults = ref<AutocompleteResult[]>([])

  /// COMPUTED
  const autocompleteEnabled = computed(() => isSearchAutocompleteEnabled())
  const searchPinsV2Enbled = computed(() => isSearchPinsV2Enabled())

  const searchInputDebounceTime = computed(() => {
    return autocompleteEnabled.value ? 100 : 400
  })

  const trimmedQueryInput = computed(() => {
    let textToSearch = ''
    if (queryInput.value !== '') {
      textToSearch = queryInput.value
    }
    return textToSearch.trim()
  })

  const currentSearchMode = computed(searchMode)

  const shouldUseSearchThisArea = computed(() => {
    return searchThisArea && searchThisAreaAccess.value
  })

  const resultMarkersEnabled = computed(() => {
    return resultMapMarkers && searchMapMarkersAccess.value
  })

  const isLocationMode = computed(
    () => currentSearchMode.value === SearchMode.Location
  )
  const isLandownerMode = computed(
    () => currentSearchMode.value === SearchMode.Landowner
  )
  const maxNumberOfResults = computed(() => {
    const numberOfResultsByMode = {
      [SearchMode.Location]: 25,
      [SearchMode.Landowner]: resultMarkersEnabled.value ? 50 : 25,
    }
    return numberOfResultsByMode[currentSearchMode.value]
  })

  const allowZeroQuery = computed(() =>
    hasMinMaxFilter(SEARCH_FILTER_KEYS.ACREAGE)
  )

  const searchLocationResults = computed<SearchResultItem[]>(locationResults)
  const searchLandownerResults = computed<SearchResultItem[]>(landownerResults)
  const searchCurrentlyActive = computed(searchIsActive)
  const groupedResults = computed(() => {
    return separateSearchResultsByCategories(searchLocationResults.value)
  })

  const searchResults = computed<SearchResultItem[]>(() => {
    // landowner search results
    if (isLandownerMode.value) {
      return searchLandownerResults.value
    }

    // location search results
    if (isLocationMode.value) {
      const resultsForGroup = groupedResults.value.find(
        (group) => group.name === currentResultsGroup.value
      )

      return resultsForGroup?.results || searchLocationResults.value
    }

    return searchLocationResults.value
  })

  const currentCamera = computed(camera)

  const formattedCameraHeight = computed(() => {
    return currentCamera.value?.location
      ? parseFloat(currentCamera.value.location.height.toFixed(1))
      : null
  })

  const isCorrectZoomForResultMarkers = computed(() => {
    if (isLandownerMode.value && currentCamera.value) {
      return currentCamera.value.location.height >= 11
    }
    return true
  })

  const baseMapRoute = computed(() => `fish-map`)

  /// METHODS
  const setError = (val: string | null) => {
    error.value = val
  }
  const setCurrentResultsGroup = (group: string) => {
    currentResultsGroup.value = group
  }

  /**
   * Handle user input from search bar
   * This is intended to be triggered by the keyup event of an HTML input
   */
  const handleInputKeyup = ({ key, target }: KeyboardEvent) => {
    const value = (target as HTMLInputElement).value

    // Ensure that the queryInput value is tracked with user input, whether or not a search request is made with that input
    setSearchCompleted(false)
    setQueryInput(value)
    setSearchOrigin(SEARCH_ORIGINS.SEARCH_BAR)

    if (key === 'Enter' && trimmedQueryInput.value !== '') {
      addQueryToHistory(trimmedQueryInput.value)

      if (autocompleteEnabled.value) {
        setSearchOrigin(SEARCH_ORIGINS.AUTOCOMPLETE_KEYBOARD)
      }

      makeSearchRequest(false)
      return
    }

    const lastSearch = isLocationMode.value
      ? lastQueryInputFetchedLocationSearch()
      : lastQueryInputFetchedLandownerSearch()

    // If the query is the same as the last fetched search term, don't perform a new search
    if (trimmedQueryInput.value === lastSearch) {
      return
    }

    makeSearchRequest(true)
  }

  /**
   * Determine if the current user input is a coordinate or text search,
   * and then perform the appropriate search request
   */
  const makeSearchRequest = debounce(function (
    isTypeAheadSearch = true,
    enforceBbox = false
  ) {
    error.value = null

    // Handle when a user deletes all text from the input
    if (trimmedQueryInput.value === '' && !allowZeroQuery.value) {
      setIsLoading(false)
      setLastQueryInputFetchedLocationSearch('')
      setLastQueryInputFetchedLandownerSearch('')
      setLocationResults([])
      setLandownerResults([])
      setTotalLocationResults(0)
      setTotalLandownerResults(0)
      setSearchCompleted(false)
      autocompleteResults.value = []
      return
    }

    // Parse the input to determine if it's a coordinate
    const {
      input,
      isDecimal,
      isDecimalHemi,
      isDdm,
      isDdmHemi,
      isDms,
      isDmsHemi,
      isUtm,
    } = parseInputForCoordinates(trimmedQueryInput.value)

    // Determine if input is a coordinate, and handle coordinate search accordingly
    // Decimal coordinate
    if (isDecimal) {
      const decimalCoord = [Number(input[0]), Number(input[1])]
      coordinateSearch(decimalCoord)
    }
    // Decimal coordinate with hemisphere initials
    else if (isDecimalHemi) {
      const latLon = parseDirectionDD(input)
      latLon ? coordinateSearch(latLon) : search(isTypeAheadSearch)
    }
    // DDM coordinate
    else if (isDdm) {
      const latLon = parseDdm(input)
      coordinateSearch(latLon)
    }
    // DDM coordinate with hemisphere initials
    else if (isDdmHemi) {
      const latLon = parseDirectionDdm(input)
      coordinateSearch(latLon)
    }
    // DMS coordinate
    else if (isDms) {
      const latLon = parseDms(input)
      // Trigger coordinate search for valid latLon, otherwise perform regular search
      latLon ? coordinateSearch(latLon) : search(isTypeAheadSearch)
    }
    // DMS coordinate with hemisphere initials
    else if (isDmsHemi) {
      const latLon = parseDirectionDms(input)
      // If there is an undefined value, do a standard search
      latLon ? coordinateSearch(latLon) : search(isTypeAheadSearch)
    }
    // UTM Coordinate
    else if (isUtm) {
      const latLon = parseUtm(input)
      latLon ? coordinateSearch(latLon) : search(isTypeAheadSearch)
    }
    // Coordinate not detected, perform text based search request
    else {
      search(isTypeAheadSearch, enforceBbox)
    }
  }, searchInputDebounceTime.value)

  /**
   * Execute a coordinate search, based on the user entered query input
   */
  const coordinateSearch = (latLon: Position) => {
    let coordinate = [Number(latLon[1]), Number(latLon[0])]

    // Check that coordinates are ordered [lon, lat] before re-centering the map
    const isLonlat = validateLonLat(coordinate[0], coordinate[1])

    if (!isLonlat) {
      coordinate = [Number(latLon[0]), Number(latLon[1])]
    }

    const [lon, lat] = coordinate

    // Validate lon and lat values by casting to a Geographic
    try {
      // Using new without assignment to validate coordinate
      new Geographic(lon, lat)
    } catch {
      // Invalid parsed coordinate, just perform regular search
      search(true)
      return
    }

    const coordinateResult = [
      {
        name: trimmedQueryInput.value,
        type: 'coordinate',
        category: '',
        location: { lat, lon },
        photo: '',
      },
    ]
    const mappedCoordinate =
      convertCoordResultToApiResponseType(coordinateResult)

    setLocationResults(mappedCoordinate)
    setSearchMode(SearchMode.Location)
    setTotalLocationResults(1)
    setLastQueryInputFetchedLocationSearch(trimmedQueryInput.value)
    setIsLoading(false)
    setSearchCompleted(true)
  }

  /**
   * Execute a standard search request, determining if it should be location or landowner search
   */
  const search = (isTypeAheadSearch: boolean, enforceBbox = false) => {
    if (isLandownerMode.value && !privateLandSearchAccess.value) {
      const defaultErrorMessage =
        'Landowner search is only available with Elite memberships.'

      error.value = defaultErrorMessage
      autocompleteRequestIndex += 1
      autocompleteResults.value = []
      setIsLoading(false)
      return
    }

    if (isTypeAheadSearch && isSearchAutocompleteEnabled()) {
      searchAutocomplete(trimmedQueryInput.value, isLandownerMode.value)
      return
    }

    setIsLoading(true)

    if (isLocationMode.value) {
      searchLocation(isTypeAheadSearch, enforceBbox)
    } else if (isLandownerMode.value) {
      searchLandowner(enforceBbox)
    }
  }

  /**
   * Perform an autocomplete search using the Autocomplete API
   */
  const searchAutocomplete = async (
    query: string,
    isPrivateLandSearch = false
  ) => {
    if (!query || query.length < 3) {
      autocompleteResults.value = []
      setLastQueryInputFetchedLandownerSearch('')
      setLastQueryInputFetchedLocationSearch('')
      removeSearchResultMarkersFromMap()
      return
    }

    autocompleteRequestIndex++
    const currentRequestIndex = autocompleteRequestIndex

    try {
      const options: AutocompleteOptions = {
        query,
        location: currentCamera.value?.location,
      }

      if (isPrivateLandSearch) {
        // Set scopes filter to private only
        options.withinScopes = 'private'

        // Set states filter to the states available to the user
        const states = getStatesFilter()
        const isoCodes = getIsoStateNames(states, false)
        options.withinStates = isoCodes.join(',')
      }

      const response = await fetchAutocompleteResults(options)

      setLocationResults([])
      setLandownerResults([])

      // Only handle response if there are no other autocomplete requests in flight
      if (currentRequestIndex === autocompleteRequestIndex) {
        // Set last query input fetched based upon the current search mode
        if (isLocationMode.value) {
          setLastQueryInputFetchedLocationSearch(query)
        } else {
          setLastQueryInputFetchedLandownerSearch(query)
        }

        autocompleteResults.value = response.result
        error.value = null
        sendSearchPerformedEvent({ query, isAutocomplete: true })
        setSearchCompleted(true)
      }
    } catch (err) {
      error.value = 'An error occurred, please try your search again.'
    }
  }

  /**
   * Perform a location search using the Supergraph API
   */
  const searchLocation = async (
    isTypeAheadSearch: boolean,
    enforceBbox = false
  ) => {
    // Capture current query input value at time of execution
    const fetchedQuery = trimmedQueryInput.value

    // Don't re-search for typeAheadSearch if the query string hasn't changed
    // If using the enforceBbox option, allow re-search with the same query string
    if (
      isTypeAheadSearch &&
      fetchedQuery === lastQueryInputFetchedLocationSearch() &&
      !enforceBbox
    ) {
      setIsLoading(false)
      return
    }

    locationRequestIndex++
    const currentLocationIndex = locationRequestIndex
    setIsLoading(true)
    autocompleteResults.value = []

    try {
      const { results, total } = await searchLocationByGraphQl(
        fetchedQuery,
        maxNumberOfResults.value,
        undefined,
        enforceBbox
      )

      // Only handle response if there are no other location requests in flight
      if (currentLocationIndex === locationRequestIndex) {
        setLocationResults(results)
        setTotalLocationResults(total)
        setLastQueryInputFetchedLocationSearch(fetchedQuery)
        sendSearchPerformedEvent({ query: fetchedQuery })
        setIsLoading(false)
        error.value = null
        setSearchCompleted(true)
      }
    } catch (err) {
      setIsLoading(false)
      error.value = 'An error occurred, please try your search again.'
    }
  }

  /**
   * Perform a landowner search using the Supergraph API
   */
  const searchLandowner = async (enforceBbox = false) => {
    /**
     * General search rules:
     * Can search text
     * Can search text && a bounding box
     * Can search text && acreage filters
     * Can search text && acreage filters && a bounding box
     * Can search EMPTY && acreage filters && a bounding box
     */
    const defaults = getFilterDefaults(
      SEARCH_FILTER_KEYS.ACREAGE
    ) as SearchFilterValues
    const currentAcreageFilterVal = acreageFilter() as SearchFilterValues
    const hasMin =
      currentAcreageFilterVal.min === defaults.min
        ? false
        : currentAcreageFilterVal.min
    const hasMax =
      currentAcreageFilterVal.max === defaults.max
        ? false
        : currentAcreageFilterVal.max

    const acreageFilterForRequest =
      hasMax || hasMin
        ? {
            area: {
              ...(hasMin && { gt: Number(hasMin) }),
              ...(hasMax && { lt: Number(hasMax) }),
            },
          }
        : {}

    let localEnforceBbox = enforceBbox
    if (allowZeroQuery.value && trimmedQueryInput.value === '') {
      // If a user tries to search an empty string with filters, force a bbox filter on
      localEnforceBbox = true
    }

    landownerRequestIndex++
    const currentLandownerIndex = landownerRequestIndex

    setIsLoading(true)
    autocompleteResults.value = []

    try {
      const fetchedQuery = trimmedQueryInput.value
      const { results, total } = await searchLandownerByGraphQl(
        trimmedQueryInput.value,
        maxNumberOfResults.value,
        undefined,
        localEnforceBbox,
        acreageFilterForRequest
      )

      // Only handle response if there are no other landowner requests in flight
      if (currentLandownerIndex === landownerRequestIndex) {
        setLandownerResults(results)
        setTotalLandownerResults(total)
        setLastQueryInputFetchedLandownerSearch(fetchedQuery)
        sendSearchPerformedEvent({ query: fetchedQuery })
        setIsLoading(false)
        error.value = null
        setSearchCompleted(true)
      }
    } catch (err) {
      setIsLoading(false)
      error.value = 'An error occurred, please try your search again.'
    }
  }

  /**
   * Send marketing event for a handled search request
   */
  const sendSearchPerformedEvent = ({
    query,
    type = null,
    group = null,
    isAutocomplete = false,
  }: {
    query: string
    type?: string | null
    group?: string | null
    isAutocomplete?: boolean
  }) => {
    const search_type = type ?? searchMode()
    const category_filter = group !== 'All' ? group : null
    const result_count = isAutocomplete
      ? autocompleteResults.value.length
      : searchResults.value.length

    marketingEventService.publish(EVENTS.SEARCH, {
      search_type,
      origin: searchOrigin(),
      query: query,
      result_count,
      category_filter,
      zoom_level: formattedCameraHeight.value,
    })
  }

  /**
   * Handle the click of a search result marker on the map, selecting that search result
   */
  const handleSearchMarkerClicked = (id: string) => {
    const resultIndex = searchResults.value.findIndex(
      (result) => result.item.id === id
    )
    const result =
      resultIndex !== -1 ? searchResults.value[resultIndex] : undefined

    if (result) {
      const resultProps: SearchResultProps = {
        ...result,
        search_rank: resultIndex,
      }
      handleResultSelected(resultProps, true)
    }
  }

  /**
   * Handling the selection of an autocomplete result, with different behavior based upon the characterization of result -
   * Suggested Search Term or Suggested Search Result
   */
  const handleAutocompleteResultSelected = async (
    result: AutocompleteResult,
    index: number
  ) => {
    const handleSearchResult = (result: SearchResultProps) => {
      handleResultSelected(result, false, true)
    }
    const handleSuggestionResult = (query: string) => {
      setSearchOrigin(
        index === 0
          ? SEARCH_ORIGINS.AUTOCOMPLETE_CURRENT_QUERY
          : SEARCH_ORIGINS.AUTOCOMPLETE_SUGGESTED_QUERY
      )
      setQueryInput(query)
      search(false)
    }

    await handleAutocompleteSelection({
      result,
      index,
      onResultSelected: handleSearchResult,
      onSuggestionSelected: handleSuggestionResult,
    })
  }

  /**
   * Handle the selection of a search result.
   */
  const handleResultSelected = (
    /** Search Result data emitted from results-list */
    result: SearchResultProps,
    fromSearchMarker = false,
    fromAutocompleteResults = false
  ) => {
    if (resultMarkersEnabled.value) {
      removeMarkersDisabled = true
    }
    handleSearchResultSelection({
      result,
      query: trimmedQueryInput.value,
      fromSearchMarker,
      fromAutocompleteResults,
      mapMarkersEnabled: resultMarkersEnabled.value,
    })
  }

  /**
   * Handle the search input being focused by the user.
   * This is intended to be triggered by the focus event of an HTML input
   */
  const handleSearchFocus = () => {
    pubsub.publish('searchFocused')

    // Send marketing event if this is an initial search intent
    if (!searchCurrentlyActive.value) {
      marketingEventService.publish(EVENTS.SEARCH_INITIATED, {
        search_history_count: searchHistoryDisplay.value?.length,
      })
    }

    setSearchIsActive(true)
    setQueryInput(activeLastSearchTerm.value)

    // Scroll results to top
    if (resultsRef.value) {
      nextTick(() => {
        resultsRef.value?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      })
      keyboardNavigation?.resetIndex()
    }

    if (onFocus) {
      // Allow for configurable behavior on focus
      onFocus()
    } else {
      // By default, route to base map and clear any active open cards
      router.push({ name: baseMapRoute.value })
    }
  }

  /**
   * Handle mousedown events to track where mouse interactions start
   */
  const mouseDownHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (target && searchRef.value) {
      mouseDownInsideSearch = searchRef.value.contains(target)
    }
  }

  /**
   * Handling a click outside of the search UI
   */
  const outsideSearchClickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null

    // Only handle click event if search is currently active
    if (target && searchCurrentlyActive.value) {
      const withinSearchClick = searchRef.value?.contains(target)
      const searchThisAreaClick = target.closest('#search-this-area')
      const mapControlClick =
        target.closest('#zoom-control') || target.closest('#control-compass')
      const backNavClick = clickedBackFromResultCard()

      // Do nothing for clicks within the search UI
      if (mouseDownInsideSearch || withinSearchClick) {
        return
      }

      // Handle clicks outside of the search UI (only if mousedown also occurred outside)
      if (searchThisAreaClick || mapControlClick || backNavClick) {
        setSearchIsActive(true)
        setClickedBackFromResultCard(false)
      } else if (!searchPinsV2Enbled.value) {
        // A click of v2 Search Pins registers as a "click outside of search", and this disabled the default outside click handling for
        // the v2 search pins.  For that experience, closing of the search UI is handled via route change
        setSearchIsActive(false)
      }

      // Always reset mousedown tracking for next interaction, regardless of whether search was closed
      mouseDownInsideSearch = false
    }
  }

  /**
   * Add markers to map for a search result set
   */
  const addSearchResultMarkersToMap = () => {
    if (
      resultMarkersEnabled.value &&
      searchResults.value.length &&
      isCorrectZoomForResultMarkers.value
    ) {
      if (searchPinsV2Enbled.value) {
        const pinFeatures = searchResults.value.map((result, index) => {
          return generatePinFeatureFromSearchResult({
            id: result.item.id,
            name: result.item.name,
            type: result.item.onxType || '',
            location: result.item.point?.coordinates ?? [0, 0],
            index,
          })
        })
        displaySearchResultPins(pinFeatures)

        return
      }

      const searchResultMarkers = generateGlyphMarkers(
        searchResults.value,
        GLYPH_FONTS.CIRCLE
      )
      replaceMarkers(searchResultMarkers)
    }
  }

  /**
   * Remove all search result markers from the map
   */
  const removeSearchResultMarkersFromMap = () => {
    if (searchPinsV2Enbled.value) {
      clearSearchResultPins()
      return
    }

    clearAllMarkers()
  }

  /**
   * Handle a search-this-area event being published
   */
  const handleSearchAreaEvent = () => {
    setSearchIsActive(true)
    setSearchOrigin(SEARCH_ORIGINS.SEARCH_AREA)
    makeSearchRequest(false, true)
  }

  /**
   * Handle the search submit button being clicked
   */
  const handleSearchSubmitClicked = () => {
    addQueryToHistory(trimmedQueryInput.value)
    makeSearchRequest(false)
  }

  /**
   * Handle a search example query being clicked
   */
  const handleExampleSearchClick = (example: string) => {
    setQueryInput(example)
    handleSearchSubmitClicked()

    // Reset keyboard index listener to navigate through search results
    keyboardNavigation?.resetIndex()
  }

  /**
   * Handle a recent search history item being clicked
   */
  const handleHistoryItemClick = (query: string) => {
    // Send marketing event
    marketingEventService.publish('recent_search_clicked', {
      query,
    })

    setQueryInput(query)
    handleSearchSubmitClicked()

    // Reset keyboard index listener to navigate through search results
    keyboardNavigation?.resetIndex()
  }

  /**
   * Handle the click of a search result group chip
   */
  const handleSearchGroupClick = (group: string) => {
    setCurrentResultsGroup(group)
    addSearchResultMarkersToMap()
    sendSearchPerformedEvent({ query: trimmedQueryInput.value, group })
    keyboardNavigation?.resetIndex()
  }

  /**
   * Sets the active state of the search bar to false when the back button on browser
   * is clicked
   */
  const browserBackHandler = () => {
    setSearchIsActive(false)
  }

  /**
   * Handler for keyboard events while search is active
   */
  const keyboardHandler = (e: KeyboardEvent) => {
    if (keyboardNavigation) {
      keyboardNavigation.callKeyFn(e.code)
    }
  }

  /**
   *  Clears results data
   */
  const clearSearch = () => {
    autocompleteResults.value = []
    setQueryInput('')
    setLastQueryInputFetchedLocationSearch('')
    setLastQueryInputFetchedLandownerSearch('')
    setLocationResults([])
    setLandownerResults([])
    setTotalLocationResults(0)
    setTotalLandownerResults(0)
    removeSearchResultMarkersFromMap()
    setError(null)
  }

  /**
   *  Resets results data and closes search UI
   */
  const resetSearch = () => {
    clearSearch()
    setSearchIsActive(false)
  }

  // LIFECYLCE EVENTS
  onMounted(() => {
    // Initialize glyph icon map for search result icons
    setGlyphIconMap()

    // Handle browser back navigation events
    window.addEventListener('popstate', () => browserBackHandler())

    // Subscribe to landowner search events
    pubsub.subscribe('search-landowner', () => {
      searchLandowner()
    })

    // Initialize the outside click handler
    document.body.addEventListener('mousedown', mouseDownHandler)
    document.body.addEventListener('click', outsideSearchClickHandler)

    // Set template refs
    // FUTURE: update to use actual template refs as params once control-search is using composition API
    searchRef.value = document.getElementById(searchId)
    if (resultsId) {
      resultsRef.value = document.getElementById(resultsId)
    }

    // Initialize keyboard nav manager for a11y
    if (searchRef.value && focusSelectors) {
      keyboardNavigation = new ComboboxNavigation(
        searchRef.value,
        focusSelectors,
        resetSearch
      )
    }
  })

  onUnmounted(() => {
    window.removeEventListener('popstate', () => browserBackHandler())
    document.body.removeEventListener('mousedown', mouseDownHandler)
    document.body.removeEventListener('click', outsideSearchClickHandler)
  })

  // WATCHERS
  watch(
    () => route?.name,
    (to) => {
      // Clear queryInput to ensure any existing search term is shown as the placeholder
      if (to !== baseMapRoute.value) {
        setQueryInput('')

        if (searchPinsV2Enbled.value) {
          // Close search UI if route changes
          setSearchIsActive(false)
        }
      }

      // Remove search-this-area from map anytime route changes
      setShowSearchArea(false)
    }
  )

  /**
   * Handle search state when search becomes active/inactive
   */
  watch(searchCurrentlyActive, (isActive) => {
    // Manage result markers
    if (resultMarkersEnabled.value) {
      if (isActive) {
        if (searchPinsV2Enbled.value) {
          // For autocomplete, remove existing markers
          if (autocompleteResults.value.length) {
            removeSearchResultMarkersFromMap()
          }

          setSearchPinSelected(null)
        }
        removeMarkersDisabled = false
        addSearchResultMarkersToMap()
      } else if (!removeMarkersDisabled) {
        removeSearchResultMarkersFromMap()
      }
    }

    // Default behavior
    if (isActive) {
      setFeatureSelected(null)
      document.addEventListener('keydown', keyboardHandler)
    } else {
      setShowSearchArea(false)
      document.removeEventListener('keydown', keyboardHandler)
    }
  })

  /**
   * Handle search state when the search result set changes
   */
  watch(searchResults, (val) => {
    if (resultMarkersEnabled.value) {
      if (val && val.length) {
        addSearchResultMarkersToMap()
      } else {
        removeSearchResultMarkersFromMap()
      }
    }
  })

  /**
   * Handle search state when isCorrectZoomForResultMarkers changes
   */
  watch(isCorrectZoomForResultMarkers, (newVal) => {
    if (resultMarkersEnabled.value && searchCurrentlyActive.value) {
      if (newVal) {
        addSearchResultMarkersToMap()
      } else {
        removeSearchResultMarkersFromMap()
      }
    }
  })

  /**
   * Setup search this area feature if option is enabled and user has access
   */
  watch(
    shouldUseSearchThisArea,
    (val) => {
      if (val) {
        pubsub.subscribe('search-this-area', () => handleSearchAreaEvent())
      } else {
        pubsub.unsubscribe('search-this-area', () => handleSearchAreaEvent())
      }
    },
    { immediate: true }
  )

  /**
   * Setup search result map markers if option is enabled and user has access
   */
  watch(
    resultMarkersEnabled,
    (val) => {
      if (val) {
        pubsub.subscribe('search-map-marker-selected', (resultId: string) => {
          handleSearchMarkerClicked(resultId)
        })
      } else {
        pubsub.unsubscribe('search-map-marker-selected', (resultId: string) => {
          handleSearchMarkerClicked(resultId)
        })
      }
    },
    { immediate: true }
  )

  /**
   * Handle search state when camera location changes
   */
  watch(
    () => currentCamera.value?.location,
    debounce(function () {
      if (shouldUseSearchThisArea.value) {
        const validTerm =
          trimmedQueryInput.value !== '' || activeLastSearchTerm.value !== ''
        const hasFilters = filterCount() > 0

        if (
          searchCurrentlyActive.value &&
          // Having filters means you can search an empty string
          (validTerm || hasFilters)
        ) {
          setShowSearchArea(true)
        }
      }
    }, 200)
  )

  /**
   * Remove search result markers when navigating away from a search result detail view
   */
  watch(routerState, (newState, oldState) => {
    if (
      oldState?.fromSearch &&
      !newState?.fromSearch &&
      !clickedBackFromResultCard() &&
      searchPinsV2Enbled.value
    ) {
      removeSearchResultMarkersFromMap()
    }
  })

  return {
    error,
    trimmedQueryInput,
    isLocationMode,
    isLandownerMode,
    autocompleteResults,
    currentResultsGroup,
    maxNumberOfResults,
    searchResults,
    groupedResults,
    setError,
    handleSearchFocus,
    handleInputKeyup,
    makeSearchRequest,
    handleAutocompleteResultSelected,
    handleResultSelected,
    handleSearchSubmitClicked,
    handleExampleSearchClick,
    handleHistoryItemClick,
    handleSearchGroupClick,
    setCurrentResultsGroup,
    sendSearchPerformedEvent,
    addSearchResultMarkersToMap,
    removeSearchResultMarkersFromMap,
    clearSearch,
    resetSearch,
  }
}

export default useMapSearch
